import {
  NutritionDeclarationType,
  PlanMealType,
  PlanMealRootItemType,
  PlanMealItemCommonType,
} from "../model/nutritionPlanTypes";

/**
 * Creates an empty NutritionDeclarationType object with all values set to 0
 */
function createEmptyNutritionDeclaration(): NutritionDeclarationType {
  return {
    quantity: 0,
    cals: 0,
    carbs: 0,
    protein: 0,
    fat: 0,
    fibers_grams: 0,
    calcium_milligrams: 0,
    vitamin_d_micrograms: 0,
    omega3_grams: 0,
    iron_milligrams: 0,
    cholesterol_milligrams: 0,
    sugars_grams: 0,
    saturated_fat_grams: 0,
    iodine_micrograms: 0,
    sodium_milligrams: 0,
    potassium_milligrams: 0,
    magnesium_milligrams: 0,
    vitamin_c_milligrams: 0,
    phenols_milligrams: 0,
    antioxidants_milligrams: 0,
    preservatives_milligrams: 0,
  };
}

/**
 * Gets the appropriate meal item considering alternatives
 * @param item The root meal item that may have alternatives
 * @returns The selected alternative if exists and is selected, otherwise the original item
 */
export function getAppropriateMealItem(
  item: PlanMealRootItemType
): PlanMealItemCommonType {
  // If item has alternatives array and one is selected, return it
  if (item.alternatives?.length > 0) {
    const selectedAlt = item.alternatives.find((alt) => alt.selected);
    if (selectedAlt) {
      return selectedAlt;
    }
  }
  return item;
}

/**
 * Adds nutritional values from source to target
 * @param target The target nutrition declaration to add to
 * @param source The source object with nutritional values
 */
function addNutritionalValues(
  target: NutritionDeclarationType,
  source: any
): void {
  if (!source) return;

  target.cals += source.cals || 0;
  target.carbs += source.carbs || 0;
  target.protein += source.protein || 0;
  target.fat += source.fat || 0;
  target.fibers_grams += source.fibers_grams || 0;
  target.calcium_milligrams += source.calcium_milligrams || 0;
  target.vitamin_d_micrograms += source.vitamin_d_micrograms || 0;
  target.omega3_grams += source.omega3_grams || 0;
  target.iron_milligrams += source.iron_milligrams || 0;
  target.cholesterol_milligrams += source.cholesterol_milligrams || 0;
  target.sugars_grams += source.sugars_grams || 0;
  target.saturated_fat_grams += source.saturated_fat_grams || 0;
  target.iodine_micrograms += source.iodine_micrograms || 0;
  target.sodium_milligrams += source.sodium_milligrams || 0;
  target.potassium_milligrams += source.potassium_milligrams || 0;
  target.magnesium_milligrams += source.magnesium_milligrams || 0;
  target.vitamin_c_milligrams += source.vitamin_c_milligrams || 0;
  target.phenols_milligrams += source.phenols_milligrams || 0;
  target.antioxidants_milligrams += source.antioxidants_milligrams || 0;
  target.preservatives_milligrams += source.preservatives_milligrams || 0;
  target.quantity += source.quantity || 0;
}

/**
 * Calculates nutritional values from a single meal item
 * @param item The meal item (with food or recipe)
 * @returns The nutritional declaration for the item
 */
function calculateItemNutrition(
  item: PlanMealItemCommonType
): NutritionDeclarationType {
  const nutrition = createEmptyNutritionDeclaration();

  if (item.food) {
    // It's a food item - add its nutritional values
    addNutritionalValues(nutrition, item.food);
  } else if (item.recipe) {
    // It's a recipe item - sum up all ingredients
    const recipe = item.recipe;
    if (recipe.items) {
      recipe.items.forEach((ingredient: any) => {
        if (ingredient.food) {
          addNutritionalValues(nutrition, ingredient.food);
        }
      });
    }
  }

  return nutrition;
}

/**
 * Calculates the total current macros for a specific meal
 * @param meal The meal to calculate macros for
 * @returns The nutritional declaration for the entire meal (current values)
 */
export function calculateMealNutrition(
  meal: PlanMealType
): NutritionDeclarationType {
  const nutrition = createEmptyNutritionDeclaration();

  if (!meal.items) return nutrition;

  // Sum up all items in the meal
  meal.items.forEach((item) => {
    // Get the appropriate item (handle alternatives)
    const appropriateItem = getAppropriateMealItem(item);

    // Check if item is not skipped
    if (!appropriateItem.is_skipped) {
      const itemNutrition = calculateItemNutrition(appropriateItem);

      // Add to totals
      Object.keys(itemNutrition).forEach((key) => {
        const nutritionKey = key as keyof NutritionDeclarationType;
        (nutrition as any)[nutritionKey] += (itemNutrition as any)[
          nutritionKey
        ];
      });
    }
  });

  return nutrition;
}

/**
 * Calculates both original declaration and current values for a meal using the deviation approach
 * @param meal The meal to calculate for
 * @returns Object containing dataCurrent (actual values) and dataOriginal (planned values)
 */
export function calculateMealDeclarations(meal: PlanMealType): {
  dataCurrent: NutritionDeclarationType;
  dataOriginal: NutritionDeclarationType;
} {
  // Calculate current values by summing all non-skipped items
  const dataCurrent = calculateMealNutrition(meal);

  // Calculate original values: dataCurrent - deviation
  const dataOriginal = createEmptyNutritionDeclaration();

  // Subtract deviation from current to get original
  // If meal.deviation is undefined, dataOriginal will be equal to dataCurrent
  Object.keys(dataCurrent).forEach((key) => {
    const nutritionKey = key as keyof NutritionDeclarationType;
    const deviationValue = meal.deviation
      ? (meal.deviation as any)[nutritionKey] || 0
      : 0;
    (dataOriginal as any)[nutritionKey] =
      (dataCurrent as any)[nutritionKey] - deviationValue;
  });

  return {
    dataCurrent,
    dataOriginal,
  };
}

/**
 * Checks if all items in a meal are skipped
 * @param meal The meal to check
 * @returns True if all items are skipped, false otherwise
 */
export function isMealFullySkipped(meal: PlanMealType): boolean {
  if (!meal.items || meal.items.length === 0) return false;

  return meal.items.every((item) => {
    const appropriateItem = getAppropriateMealItem(item);
    return appropriateItem.is_skipped;
  });
}
